import { useEffect, useRef, useState } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuthStore } from '../../stores/authStore';
import { Avatar } from '../common/Avatar';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { getFirebaseStorage } from '../../services/firebase/config';
import { isValidDisplayName } from '../../utils/validation';
import { updateLocalParticipantProfile } from '../../services/livekit';

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Profile settings panel for updating the user's avatar image.
 */
export function ProfileSettingsPanel() {
  const { user, updateAvatar, updateDisplayName } = useAuthStore();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(user?.avatarUrl || null);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);
  const [isSavingName, setIsSavingName] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPreviewUrl(user?.avatarUrl || null);
    setDisplayName(user?.displayName || '');
  }, [user?.avatarUrl, user?.displayName]);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleSelectClick = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  const handleDisplayNameSave = async () => {
    if (!user) {
      return;
    }

    const trimmed = displayName.trim();
    if (!isValidDisplayName(trimmed)) {
      setDisplayNameError('Display name must be 2-50 characters and use letters or numbers.');
      return;
    }

    setDisplayNameError(null);
    setIsSavingName(true);
    try {
      await updateDisplayName(trimmed);
      updateLocalParticipantProfile({ displayName: trimmed });
    } catch (saveError) {
      const message =
        saveError instanceof Error ? saveError.message : 'Update failed';
      setDisplayNameError(message);
    } finally {
      setIsSavingName(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file || !user) {
      if (input.value) {
        input.value = '';
      }
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`Image must be under ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(nextPreviewUrl);
    setIsUploading(true);
    setError(null);

    try {
      const storage = getFirebaseStorage();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storageRef = ref(
        storage,
        `profile-images/${user.uid}/${Date.now()}-${sanitizedName}`
      );

      await uploadBytes(storageRef, file, { contentType: file.type });
      const downloadUrl = await getDownloadURL(storageRef);
      await updateAvatar(downloadUrl);
      updateLocalParticipantProfile({ avatarUrl: downloadUrl });
    } catch (uploadError) {
      const message =
        uploadError instanceof Error ? uploadError.message : 'Upload failed';
      setError(message);
    } finally {
      setIsUploading(false);
      input.value = '';
    }
  };

  return (
    <section className="card p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-2">Display Name</h2>
        <p className="text-sm text-gray-400 mb-4">
          Shown to others in meetings and room lists.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your display name"
            error={displayNameError || undefined}
            className="flex-1"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDisplayNameSave}
            isLoading={isSavingName}
            disabled={!user || displayName.trim() === (user?.displayName || '')}
          >
            Save
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Profile Photo</h2>
          <p className="text-sm text-gray-400">
            Used when your camera is off and in room lists.
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={handleSelectClick}
          isLoading={isUploading}
          disabled={!user}
        >
          Upload
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <Avatar
          name={user?.displayName || 'User'}
          imageUrl={previewUrl || undefined}
          size="xl"
          className="border border-border"
        />
        <div className="text-sm text-gray-400">
          <p>PNG or JPG up to {MAX_FILE_SIZE_MB}MB.</p>
          <p className="mt-1">We default to your Google profile photo when available.</p>
        </div>
      </div>

      {error && (
        <p className="text-danger text-sm mt-4">{error}</p>
      )}
    </section>
  );
}
